import { Card } from "../Primitives";
import { SkeletonPanel } from "../Skeletons";
import { formatDate } from "../crmHelpers";
import { AREA_LABELS, WORK_MODE_LABELS, labelOf } from "../../events/eventHelpers";

/*
  Quem é a conta. As respostas já estavam no banco: a pesquisa de perfil que o app faz
  e as perguntas do cadastro. Só não apareciam em tela nenhuma.

  Os rótulos repetem os do app (ProfileSurveyModal, em front-end). Valor que não estiver
  aqui aparece cru, em vez de sumir.
*/

const SURVEY_LABELS = {
  audienceSize: {
    UNDER_1K: "Até 1 mil",
    "1K_5K": "1 a 5 mil",
    "5K_20K": "5 a 20 mil",
    "20K_100K": "20 a 100 mil",
    OVER_100K: "Mais de 100 mil",
  },
  wantsToAppear: {
    YES: "Sim, com certeza",
    MAYBE: "Talvez",
    NO: "Prefere não aparecer",
  },
  whereYouWork: {
    OWN_SALON: "Salão próprio",
    CHAIR_RENTAL: "Aluguel de cadeira",
    HOME_STUDIO: "Estúdio em casa",
    HOUSE_CALL: "Domicílio",
  },
  producesContent: {
    WEEKLY: "Toda semana",
    SOMETIMES: "Às vezes",
    RARELY: "Quase nunca",
    NOT_YET: "Ainda não",
  },
  beyondAttending: {
    TALKS: "Dá palestras",
    COURSES: "Dá cursos",
    MENTORING: "Faz mentorias",
    BRAND_EDUCATOR: "Educadora de marca",
    PODCAST: "Tem podcast ou canal",
    EVENTS: "Organiza eventos",
    NONE: "Nenhuma ainda",
  },
  monthlyReach: {
    UP_TO_20: "Até 20",
    "20_TO_50": "20 a 50",
    "50_TO_200": "50 a 200",
    OVER_200: "Mais de 200",
  },
};

const CHANNEL_LABELS = {
  instagram: "Instagram",
  tiktok: "TikTok",
  indicacao: "Indicação",
  google: "Google",
  loja: "Loja de apps",
  outro: "Outro",
};

const surveyLabel = (field, value) =>
  value ? SURVEY_LABELS[field]?.[value] || value : null;

const Row = ({ label, value }) =>
  value ? (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="shrink-0 text-xs text-slate-500">{label}</span>
      <span className="text-right text-xs font-semibold text-slate-800">{value}</span>
    </div>
  ) : null;

const ProfilePanel = ({ insights }) => {
  if (insights.isLoading) return <SkeletonPanel lines={5} />;

  // A ficha continua útil sem este bloco: o erro aparece no mapa de adoção.
  const profile = insights.data?.profile;
  if (!profile) return null;

  const { survey, onboarding } = profile;
  const instagram = survey?.instagramHandle || profile.instagram;
  const hasOnboarding = onboarding?.area || onboarding?.workMode;

  return (
    <Card className="p-4">
      <h2 className="text-sm font-bold text-slate-900">Perfil</h2>
      <p className="mb-2 text-[11px] text-slate-500">
        Respostas do cadastro e da pesquisa do app
      </p>

      <div className="divide-y divide-slate-100">
        <Row label="Área" value={labelOf(AREA_LABELS, onboarding?.area) || survey?.specialty} />
        <Row label="Trabalha" value={labelOf(WORK_MODE_LABELS, onboarding?.workMode)} />
        <Row label="Canal" value={CHANNEL_LABELS[profile.acquisitionSource] || profile.acquisitionSource} />
        <Row label="Instagram" value={instagram ? `@${String(instagram).replace(/^@/, "")}` : null} />
        <Row label="Onde atende" value={surveyLabel("whereYouWork", survey?.whereYouWork)} />
        <Row label="Audiência" value={surveyLabel("audienceSize", survey?.audienceSize)} />
        <Row label="Produz conteúdo" value={surveyLabel("producesContent", survey?.producesContent)} />
        <Row
          label="Além de atender"
          value={(survey?.beyondAttending || [])
            .map((item) => surveyLabel("beyondAttending", item))
            .join(", ")}
        />
        <Row label="Alcança por mês" value={surveyLabel("monthlyReach", survey?.monthlyReach)} />
        <Row label="Aparecer em conteúdo" value={surveyLabel("wantsToAppear", survey?.wantsToAppear)} />
        <Row label="WhatsApp informado" value={survey?.whatsapp} />
      </div>

      {survey?.status === "DECLINED" ? (
        <p className="mt-2 text-[11px] text-slate-500">
          Escolheu "Agora não" na pesquisa do app
          {survey.submittedAt ? ` em ${formatDate(survey.submittedAt)}` : ""}.
        </p>
      ) : null}
      {survey?.answered && survey.submittedAt ? (
        <p className="mt-2 text-[11px] text-slate-400">
          Pesquisa respondida em {formatDate(survey.submittedAt)}.
        </p>
      ) : null}
      {!survey && !hasOnboarding && !profile.acquisitionSource && !instagram ? (
        <p className="text-xs text-slate-500">Nenhuma resposta registrada ainda.</p>
      ) : null}
    </Card>
  );
};

export default ProfilePanel;
